import { colors } from '../constants';
import {
    type DynamicTokens,
    type DynamicTokensOverrides,
    getDynamicTokens,
    type Token,
} from './tokens';
import { createThemeMap, forEachVariants, type KeyedVariantsValue } from './variants';

/**
 * This type is a bit of a hack to define the shape of the theme.
 * To add a new token, add it here first and then in the theme value below (TypeScript will complain if you don't)
 */
export type Theme<S = string, N = number> = {
    colors: {
        background: {
            main: S;
            mid: S;
            muted: S;
            standout: S;
            inverted: S;
        };
        sidebar: {
            background: S;
            activeItem: S;
            hoverItem: S;
            itemBorder: S;
            itemText: S;
            mutedItemText: S;
            activeItemText: S;
            inputBorder: S;
            standoutText: S;
        };
        border: {
            main: S;
            mid: S;
            muted: S;
            secondary: S;
        };
        text: {
            main: S;
            mid: S;
            muted: S;
            light: S;
            standout: S;
            inverted: S;
            primary: S;
        };

        primary: {
            main: S;
            mid: S;
            muted: S;
            hovered: S;
            standout: S;
            textInside: S;
        };

        secondary: {
            main: S;
            muted: S;
            hovered: S;
            standout: S;
            textInside: S;
        };

        standout: {
            main: S;
            standout: S;
            textInside: S;
        };

        itemHover: S;
        backdrop: {
            background: S;
            backgroundLight: S;
            transparent: S;
        };

        info: {
            main: S;
            muted: S;
            hovered: S;
            standout: S;
            textInside: S;
        };

        success: {
            main: S;
            muted: S;
            standout: S;
            textInside: S;
        };

        warning: {
            main: S;
            muted: S;
            standout: S;
            textInside: S;
        };

        danger: {
            main: S;
            muted: S;
            hovered: S;
            standout: S;
            textInside: S;
        };

        merge: {
            main: S;
            muted: S;
            hovered: S;
            standout: S;
            textInside: S;
        };

        upgrade: {
            main: S;
            muted: S;
            hovered: S;
            standout: S;
            textInside: S;
        };
    };
    shadows: {
        base: {
            shadowColor: S;
            shadowOffset: {
                width: N;
                height: N;
            };
            shadowOpacity: N;
            shadowRadius: N;
            elevation: N;
        };

        hover: {
            shadowColor: S;
            shadowOffset: {
                width: N;
                height: N;
            };
            shadowOpacity: N;
            shadowRadius: N;
            elevation: N;
        };

        spread: {
            shadowColor: S;
            shadowOffset: {
                width: N;
                height: N;
            };
            shadowOpacity: N;
            shadowRadius: N;
            elevation: N;
        };

        pop: {
            shadowColor: S;
            shadowOffset: {
                width: N;
                height: N;
            };
            shadowOpacity: N;
            shadowRadius: N;
            elevation: N;
        };

        tight: {
            shadowColor: S;
            shadowOffset: {
                width: N;
                height: N;
            };
            shadowOpacity: N;
            shadowRadius: N;
            elevation: N;
        };
    };
    radiuses: {
        medium: N;
        large: N;
        xlarge: N;
    };
};

export const theme: Theme<Token<string>, Token<number>> = {
    colors: {
        background: {
            main: {
                value: colors.WHITE,
                overrides: [
                    {
                        color: 'dark',
                        value: colors.darkModeGreyScale.xdark,
                    },
                ],
            },
            mid: {
                value: colors.lightModeGreyScale.xxlight,
                overrides: [
                    {
                        color: 'dark',
                        value: colors.darkModeGreyScale.xdark,
                    },
                ],
            },
            muted: {
                value: colors.lightModeGreyScale.xxlight,
                overrides: [
                    {
                        color: 'dark',
                        value: colors.darkModeGreyScale.base,
                    },
                ],
            },
            standout: {
                value: colors.lightModeGreyScale.xlight,
                overrides: [
                    {
                        color: 'dark',
                        value: colors.darkModeGreyScale.xlight,
                    },
                ],
            },
            inverted: {
                value: colors.darkModeGreyScale.dark,
                overrides: [
                    {
                        color: 'dark',
                        value: colors.WHITE,
                    },
                ],
            },
        },

        sidebar: {
            background: {
                value: colors.lightModeSidebar.base,
                overrides: [
                    {
                        color: 'light',
                        contrast: 'low',
                        value: colors.WHITE,
                    },
                    {
                        color: 'dark',
                        value: colors.darkModeGreyScale.dark,
                    },
                ],
            },
            activeItem: {
                value: colors.lightModeSidebar.dark,
                overrides: [
                    {
                        color: 'light',
                        contrast: 'low',
                        value: colors.lightModeGreyScale.xlight,
                    },
                    {
                        color: 'dark',
                        value: colors.BLACK,
                    },
                ],
            },
            hoverItem: {
                value: 'rgba(0,0,0,0.1)',
                overrides: [
                    {
                        color: 'light',
                        contrast: 'low',
                        value: colors.lightModeGreyScale.xxlight,
                    },
                    {
                        color: 'dark',
                        value: 'rgba(0,0,0,.2)',
                    },
                ],
            },
            itemBorder: {
                value: colors.lightModeSidebar.dark,
                overrides: [
                    {
                        color: 'light',
                        contrast: 'low',
                        value: colors.lightModeGreyScale.light,
                    },
                    {
                        color: 'dark',
                        value: colors.darkModeGreyScale.xlight,
                    },
                ],
            },
            itemText: {
                value: colors.darkModeText.dark,
                overrides: [
                    {
                        color: 'light',
                        contrast: 'low',
                        value: colors.lightModeText.light,
                    },
                    {
                        color: 'dark',
                        value: colors.darkModeText.dark,
                    },
                ],
            },
            mutedItemText: {
                value: colors.darkModeText.xdark,
                overrides: [
                    {
                        color: 'light',
                        contrast: 'low',
                        value: colors.lightModeText.xlight,
                    },
                    {
                        color: 'dark',
                        value: colors.darkModeText.xdark,
                    },
                ],
            },
            activeItemText: {
                value: colors.WHITE,
                overrides: [
                    {
                        color: 'light',
                        contrast: 'low',
                        value: colors.BLACK,
                    },
                    {
                        color: 'dark',
                        value: colors.WHITE,
                    },
                ],
            },
            inputBorder: {
                value: colors.lightModeSidebar.dark,
                overrides: [
                    {
                        color: 'light',
                        contrast: 'low',
                        value: colors.lightModeGreyScale.base,
                    },
                    {
                        color: 'dark',
                        value: colors.darkModeGreyScale.light,
                    },
                ],
            },
            standoutText: {
                value: colors.WHITE,
                overrides: [
                    {
                        color: 'light',
                        contrast: 'low',
                        value: colors.BLACK,
                    },
                    {
                        color: 'dark',
                        value: colors.WHITE,
                    },
                ],
            },
        },

        border: {
            main: {
                value: colors.lightModeGreyScale.base,
                overrides: [
                    {
                        color: 'dark',
                        value: colors.darkModeGreyScale.xxlight,
                    },
                ],
            },
            mid: {
                value: colors.lightModeGreyScale.light,
                overrides: [
                    {
                        color: 'dark',
                        value: colors.darkModeGreyScale.light,
                    },
                ],
            },
            muted: {
                value: colors.lightModeGreyScale.xxlight,
                overrides: [
                    {
                        color: 'dark',
                        value: colors.BLACK,
                    },
                ],
            },
            // Matches our secondary border in Spine, but #dedfe3' is not a shade in uikit.
            secondary: {
                value: '#dedfe3',
                overrides: [
                    {
                        color: 'dark',
                        value: colors.darkModeGreyScale.light,
                    },
                ],
            },
        },

        text: {
            main: {
                value: colors.lightModeText.base,
                overrides: [
                    {
                        color: 'dark',
                        value: colors.darkModeText.base,
                    },
                ],
            },
            mid: {
                value: colors.lightModeText.light,
                overrides: [
                    {
                        color: 'dark',
                        value: colors.darkModeText.dark,
                    },
                ],
            },
            muted: {
                value: 'var(--sp-color-text-muted)',
                overrides: [
                    {
                        color: 'dark',
                        value: 'var(--sp-color-text-muted)',
                    },
                ],
            },
            light: {
                value: 'var(--sp-color-text-light)',
                overrides: [
                    {
                        color: 'dark',
                        value: 'var(--sp-color-text-light)',
                    },
                ],
            },
            standout: {
                value: colors.lightModeText.dark,
                overrides: [
                    {
                        color: 'dark',
                        value: colors.darkModeText.light,
                    },
                ],
            },
            inverted: {
                value: colors.darkModeText.base,
                overrides: [
                    {
                        color: 'dark',
                        value: colors.lightModeText.base,
                    },
                ],
            },
            primary: {
                value: (dynamicTokens) => dynamicTokens.colors.primary.base,
                overrides: [
                    {
                        color: 'dark',
                        value: (dynamicTokens) => dynamicTokens.colors.primary.light,
                    },
                ],
            },
        },

        primary: {
            main: {
                value: (dynamicTokens) => dynamicTokens.colors.primary.base,
                overrides: [
                    {
                        color: 'dark',
                        value: (dynamicTokens) => dynamicTokens.colors.primary.base,
                    },
                ],
            },
            mid: {
                value: (dynamicTokens) => dynamicTokens.colors.primary.xlight,
                overrides: [
                    {
                        color: 'dark',
                        value: (dynamicTokens) => dynamicTokens.colors.primary.xdark,
                    },
                ],
            },
            muted: {
                value: (dynamicTokens) => dynamicTokens.colors.primary.xxlight,
                overrides: [
                    {
                        color: 'dark',
                        value: colors.BLACK, // Dark mode uses a limited primary palette and falls back to greys to avoid colour clashes
                    },
                ],
            },
            hovered: {
                value: (dynamicTokens) => dynamicTokens.colors.primary.dark,
                overrides: [
                    {
                        color: 'dark',
                        value: (dynamicTokens) => dynamicTokens.colors.primary.base,
                    },
                ],
            },
            standout: {
                value: (dynamicTokens) => dynamicTokens.colors.primary.xdark,
                overrides: [
                    {
                        color: 'dark',
                        value: (dynamicTokens) => dynamicTokens.colors.primary.dark,
                    },
                ],
            },
            textInside: {
                value: colors.WHITE,
                overrides: [
                    {
                        color: 'dark',
                        value: (dynamicTokens) => dynamicTokens.colors.primary.xxdark,
                    },
                ],
            },
        },

        secondary: {
            main: {
                value: colors.WHITE,
                overrides: [
                    {
                        color: 'dark',
                        value: colors.darkModeGreyScale.xlight,
                    },
                ],
            },
            muted: {
                value: colors.lightModeGreyScale.xlight,
                overrides: [
                    {
                        color: 'dark',
                        value: colors.darkModeGreyScale.dark,
                    },
                ],
            },
            hovered: {
                value: colors.lightModeGreyScale.xlight,
                overrides: [
                    {
                        color: 'dark',
                        value: colors.darkModeGreyScale.xxlight,
                    },
                ],
            },
            standout: {
                value: colors.lightModeGreyScale.light,
                overrides: [
                    {
                        color: 'dark',
                        value: colors.darkModeGreyScale.xxlight,
                    },
                ],
            },
            textInside: {
                value: colors.lightModeGreyScale.xdark,
                overrides: [
                    {
                        color: 'dark',
                        value: colors.WHITE,
                    },
                ],
            },
        },

        standout: {
            main: {
                value: (dynamicTokens) => dynamicTokens.colors.primary.base,
                overrides: [
                    {
                        color: 'dark',
                        value: colors.WHITE,
                    },
                ],
            },
            standout: {
                value: (dynamicTokens) => dynamicTokens.colors.primary.dark,
                overrides: [{ color: 'dark', value: colors.lightModeGreyScale.xxlight }],
            },
            textInside: {
                value: colors.WHITE,
                overrides: [
                    {
                        color: 'dark',
                        value: (dynamicTokens) => dynamicTokens.colors.primary.base,
                    },
                ],
            },
        },

        itemHover: {
            value: colors.lightModeGreyScale.xxlight,
            overrides: [{ color: 'dark', value: colors.darkModeGreyScale.light }],
        },

        backdrop: {
            transparent: colors.BACKDROP_BG_TRANSPARENT,
            background: {
                value: colors.BACKDROP_BG,
                overrides: [{ color: 'dark', value: colors.BACKDROP_DARK_BG }],
            },
            backgroundLight: {
                value: colors.BACKDROP_BG_LIGHT,
                overrides: [{ color: 'dark', value: colors.BACKDROP_DARK_BG_LIGHT }],
            },
        },

        info: {
            main: colors.blue.base,
            muted: colors.blue.xlight,
            hovered: {
                value: colors.blue.dark,
                overrides: [{ color: 'dark', value: colors.blue.light }],
            },
            standout: colors.blue.xdark,
            textInside: colors.WHITE,
        },

        success: {
            main: colors.green.base,
            muted: {
                value: colors.green.xxlight,
                overrides: [{ color: 'dark', value: colors.green.xdark }],
            },
            standout: {
                value: colors.green.dark,
                overrides: [{ color: 'dark', value: colors.green.light }],
            },
            textInside: colors.WHITE,
        },

        warning: {
            main: colors.yellow.base,
            muted: {
                value: colors.yellow.xlight,
                overrides: [{ color: 'dark', value: colors.yellow.xdark }],
            },
            standout: {
                value: colors.yellow.dark,
                overrides: [{ color: 'dark', value: colors.yellow.light }],
            },
            textInside: colors.WHITE,
        },

        danger: {
            main: colors.scarlet.base,
            muted: colors.scarlet.xlight,
            hovered: {
                value: colors.scarlet.dark,
                overrides: [{ color: 'dark', value: colors.scarlet.base }],
            },
            standout: colors.scarlet.xdark,
            textInside: colors.WHITE,
        },

        merge: {
            main: colors.violet.base,
            muted: colors.violet.xlight,
            hovered: colors.violet.dark,
            standout: colors.violet.xdark,
            textInside: colors.WHITE,
        },

        upgrade: {
            main: colors.fuchsia.base,
            muted: colors.fuchsia.xlight,
            hovered: colors.fuchsia.dark,
            standout: colors.fuchsia.xdark,
            textInside: colors.WHITE,
        },
    },

    shadows: {
        base: {
            shadowColor: '#000',
            shadowOffset: {
                width: 0,
                height: 1,
            },
            shadowOpacity: {
                value: 0.12,
                overrides: [
                    {
                        color: 'dark',
                        value: 0.69,
                    },
                ],
            },
            shadowRadius: 2,
            elevation: 5,
        },

        hover: {
            shadowColor: '#000',
            shadowOffset: {
                width: 0,
                height: 12,
            },
            shadowOpacity: {
                value: 0.02,
                overrides: [
                    {
                        color: 'dark',
                        value: 0.12,
                    },
                ],
            },
            shadowRadius: 13,
            elevation: 4,
        },

        spread: {
            shadowColor: '#000',
            shadowOffset: {
                width: 0,
                height: 0,
            },
            shadowOpacity: {
                value: 0.04,
                overrides: [
                    {
                        color: 'dark',
                        value: 0.52,
                    },
                ],
            },
            shadowRadius: 7,
            elevation: 2,
        },

        pop: {
            shadowColor: '#000',
            shadowOffset: {
                width: 0,
                height: 4,
            },
            shadowOpacity: {
                value: 0.05,
                overrides: [
                    {
                        color: 'dark',
                        value: 0.99,
                    },
                ],
            },
            shadowRadius: 10,

            elevation: 5,
        },

        tight: {
            shadowColor: '#000',
            shadowOffset: {
                width: 0,
                height: 1,
            },
            shadowOpacity: {
                value: 0.12,
                overrides: [
                    {
                        color: 'dark',
                        value: 0.69,
                    },
                ],
            },
            shadowRadius: 1,
            elevation: 2,
        },
    },
    radiuses: {
        medium: {
            value: 4,
            overrides: [{ radius: 'straight', value: 0 }],
        },
        large: {
            value: 6,
            overrides: [{ radius: 'straight', value: 1 }],
        },
        xlarge: {
            value: 8,
            overrides: [{ radius: 'straight', value: 3 }],
        },
    },
};

export const themeMap = createThemeMap<
    Theme<Token<string>, Token<number>>,
    Theme<
        string | ((dynamicTokens: DynamicTokens) => string),
        number | ((dynamicTokens: DynamicTokens) => number)
    >
>(theme);

/**
 * Given a theme and the current values for the dynamic tokens, returns
 * a theme that doesn't depend on the dynamic tokens anymore.
 */
export function resolveTheme(
    theme: Theme<
        string | ((dynamicTokens: DynamicTokens) => string),
        number | ((dynamicTokens: DynamicTokens) => number)
    >,
    dynamicTokens: DynamicTokens
): Theme {
    // @ts-ignore
    const resolvedTheme: Theme = {};
    Object.keys(theme).forEach((k) => {
        const value = theme[k];
        if (typeof value === 'function') {
            resolvedTheme[k] = value(dynamicTokens);
        } else if (typeof value === 'object') {
            resolvedTheme[k] = resolveTheme(value, dynamicTokens);
        } else {
            resolvedTheme[k] = value;
        }
    });
    return resolvedTheme;
}

export const resolvedThemeMap = new WeakMap<
    DynamicTokensOverrides,
    Map<KeyedVariantsValue, Theme>
>();

/**
 * Given a theme and the current values for the dynamic tokens, returns
 * a theme that doesn't depend on the dynamic tokens anymore.
 *
 * If we haven't yet computed it, we are going to compute all the themes for all
 * the variant permutations for this given set of dynamic tokens and cache them.
 */
export function getResolvedTheme(
    theme: KeyedVariantsValue,
    dynamicTokensOverrides: DynamicTokensOverrides
): Theme {
    const existingResolution = resolvedThemeMap.get(dynamicTokensOverrides);
    const existingTheme = existingResolution?.get(theme);
    if (existingTheme) {
        return existingTheme;
    }

    const resolvedTheme = existingResolution || new Map<KeyedVariantsValue, Theme>();
    const dynamicTokens = getDynamicTokens(dynamicTokensOverrides);

    forEachVariants((_variant, keyedVariant) => {
        const mappedTheme = themeMap.get(keyedVariant);
        if (mappedTheme) {
            resolvedTheme.set(keyedVariant, resolveTheme(mappedTheme, dynamicTokens));
        }
    });

    resolvedThemeMap.set(dynamicTokensOverrides, resolvedTheme);

    const resolved = resolvedTheme.get(theme);
    if (!resolved) {
        throw new Error('Resolved theme not found for provided variants');
    }
    return resolved;
}
